import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { CaretLeft, CaretRight } from 'phosphor-react';
import { Button } from './Button';
import { Slider } from './Slider';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './Tooltip';
import { useAuthToken } from '../utils/auth';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

interface HighlightRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

// Configurar el worker de react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface PDFViewerProps {
  file?: File;
  url?: string;
  fileName?: string;
  highlightText?: string | null;
  highlightPage?: number; // 1-based; if set, navigate here before searching
  highlightReqId?: number; // unique ID to trigger same-page repeats
}

export const PDFViewer: React.FC<PDFViewerProps> = ({ file, url, fileName, highlightText, highlightPage, highlightReqId }) => {
  const { getToken } = useAuthToken();
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.2);
  // true until the page canvas has fully painted
  const [pageReady, setPageReady] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [pdfOptions, setPdfOptions] = useState<any>(undefined);
  // false until options are resolved so we don't mount Document prematurely
  const [optionsReady, setOptionsReady] = useState(!url);
  const [highlightRects, setHighlightRects] = useState<HighlightRect[]>([]);
  const [highlightMatchFailed, setHighlightMatchFailed] = useState(false);
  const pageWrapperRef = useRef<HTMLDivElement>(null);
  // pdfjs document proxy kept for text searches
  const pdfjsDocRef = useRef<any>(null);

  // Estados para drag
  const [isDragging, setIsDragging] = useState(false);
  const dragStartRef = useRef({ x: 0, y: 0, scrollX: 0, scrollY: 0 });
  const containerRef = useRef<HTMLDivElement>(null);

  const pdfSource = file || url;

  useEffect(() => {
    setPageReady(false);
    setHasError(false);
    setNumPages(0);
    setPageNumber(1);
    if (url) {
      setOptionsReady(false);
      getToken().then(token => {
        setPdfOptions(token ? { httpHeaders: { Authorization: `Bearer ${token}` } } : undefined);
        setOptionsReady(true);
      });
    } else {
      setPdfOptions(undefined);
      setOptionsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, file]);

  // Load pdfjs doc proxy whenever the source changes so we can call getTextContent
  useEffect(() => {
    if (!pdfSource) { pdfjsDocRef.current = null; return; }
    const loadDoc = async () => {
      try {
        const src = pdfSource instanceof File
          ? await pdfSource.arrayBuffer()
          : { url: pdfSource, httpHeaders: pdfOptions?.httpHeaders };
        const loadingTask = pdfjs.getDocument(src as any);
        pdfjsDocRef.current = await loadingTask.promise;
      } catch {
        pdfjsDocRef.current = null;
      }
    };
    loadDoc();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfSource, pdfOptions]);

  const searchAndHighlight = useCallback(async (text: string, targetPage?: number) => {
    setHighlightRects([]);
    setHighlightMatchFailed(false);
    const doc = pdfjsDocRef.current;
    if (!doc || !pageWrapperRef.current) return;

    const needle = text.trim().toLowerCase();
    if (!needle) return;

    // Search current page first, then all pages to find which page has the match
    const totalPages = doc.numPages as number;
    let foundPage: number | null = null;
    let foundItems: any[] = [];
    let viewport: any = null;

    const tryPage = async (pNum: number) => {
      const page = await doc.getPage(pNum);
      const vp = page.getViewport({ scale });
      const content = await page.getTextContent();
      const items: any[] = content.items as any[];

      // Build concatenated string to allow matches that span multiple items
      let concat = '';
      const offsets: { start: number; item: any }[] = [];
      for (const item of items) {
        offsets.push({ start: concat.length, item });
        concat += item.str;
      }

      const idx = concat.toLowerCase().indexOf(needle);
      if (idx === -1) return false;

      // Find which items overlap the match range
      const matchEnd = idx + needle.length;
      const matching = offsets.filter(({ start, item }) => {
        const end = start + item.str.length;
        return start < matchEnd && end > idx;
      });

      foundPage = pNum;
      foundItems = matching.map(o => o.item);
      viewport = vp;
      return true;
    };

    // If targetPage is specified, try that page first
    const startPage = targetPage || pageNumber;
    let found = await tryPage(startPage);
    if (!found) {
      for (let p = 1; p <= totalPages; p++) {
        if (p === startPage) continue;
        found = await tryPage(p);
        if (found) break;
      }
    }

    if (!found || foundItems.length === 0 || !viewport) {
      console.warn('[PDFViewer] highlight: no match found for', JSON.stringify(text));
      setHighlightMatchFailed(true);
      return;
    }

    // Navigate to the page that has the match
    if (foundPage !== pageNumber) {
      setPageNumber(foundPage!);
      // highlightRects will be recomputed by the pageReady effect below
      return;
    }

    // pdfjs transform: [scaleX, skewY, skewX, scaleY, tx, ty]
    // ty is measured from bottom-left of page; we need top-left for CSS
    const pageHeight = viewport.height;
    const rects: HighlightRect[] = foundItems.map(item => {
      const [, , , , tx, ty] = item.transform as number[];
      const x = tx * scale;
      const y = pageHeight - (ty * scale) - (item.height * scale);
      return { x, y, width: item.width * scale, height: item.height * scale };
    });

    setHighlightRects(rects);

    // Scroll the first rect into view
    if (pageWrapperRef.current && rects.length > 0) {
      const el = pageWrapperRef.current;
      const container = el.closest('.overflow-auto') as HTMLElement | null;
      if (container) {
        const elTop = el.offsetTop + rects[0].y;
        const elLeft = el.offsetLeft + rects[0].x;
        container.scrollTo({
          top: elTop - container.clientHeight / 2,
          left: elLeft - container.clientWidth / 2,
          behavior: 'smooth',
        });
      }
    }
  }, [pageNumber, scale]);

  // Navigate to highlightPage if specified
  useEffect(() => {
    if (highlightPage && highlightPage !== pageNumber) {
      setPageNumber(highlightPage);
    }
  }, [highlightPage, pageNumber]);

  // Re-run search when highlightText changes, when we land on the right page, or when highlightReqId changes
  useEffect(() => {
    if (!highlightText || !pageReady) {
      setHighlightRects([]);
      setHighlightMatchFailed(false);
      return;
    }
    // Only search if we're on the target page or if no specific target page was requested
    if (highlightPage && highlightPage !== pageNumber) {
      return;
    }
    searchAndHighlight(highlightText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightText, highlightPage, highlightReqId, pageReady]);

  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));

  const handleSliderChange = (values: number[]) => {
    if (values?.length > 0 && !isNaN(values[0])) {
      setScale(Math.max(0.5, Math.min(3.0, values[0])));
    }
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (
      target.closest('button') ||
      target.closest('[role="slider"]') ||
      target.closest('[data-orientation]') ||
      target.closest('[data-radix-slider-thumb]') ||
      target.closest('[data-radix-slider-track]') ||
      target.closest('[data-radix-slider-range]')
    ) return;
    if (e.button !== 0 || !containerRef.current) return;
    setIsDragging(true);
    dragStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      scrollX: containerRef.current.scrollLeft,
      scrollY: containerRef.current.scrollTop,
    };
    e.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;
    const handleMouseMove = (e: MouseEvent) => {
      if (!containerRef.current) return;
      containerRef.current.scrollLeft = dragStartRef.current.scrollX - (e.clientX - dragStartRef.current.x);
      containerRef.current.scrollTop = dragStartRef.current.scrollY - (e.clientY - dragStartRef.current.y);
    };
    const handleMouseUp = () => setIsDragging(false);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  const Skeleton = () => (
    <div className="w-full flex flex-col gap-3 px-2">
      <div className="h-6 w-1/3 bg-muted rounded animate-pulse" />
      <div className="h-4 w-full bg-muted rounded animate-pulse" />
      <div className="h-4 w-5/6 bg-muted rounded animate-pulse" />
      <div className="h-4 w-full bg-muted rounded animate-pulse" />
      <div className="h-4 w-4/6 bg-muted rounded animate-pulse" />
      <div className="h-4 w-full bg-muted rounded animate-pulse" />
      <div className="h-4 w-3/4 bg-muted rounded animate-pulse" />
    </div>
  );

  return (
    <div className="flex flex-col h-full w-full">
      {/* Toolbar */}
      <div className="flex items-center px-4 py-2 border-b border-border bg-muted shrink-0 gap-2">
        <div className="flex-[5] min-w-0">
          {fileName && (
            <span className="text-xs text-text-subtle font-sans truncate block" title={fileName}>
              {fileName}
            </span>
          )}
        </div>

        {numPages > 1 && (
          <div className="flex items-center justify-center gap-2 shrink-0">
            {pageNumber > 1 && (
              <Button variant="ghost" size="icon" onClick={goToPrevPage}>
                <CaretLeft size={16} weight="bold" />
              </Button>
            )}
            <span className="text-sm text-text font-sans font-medium">
              {pageNumber} / {numPages}
            </span>
            {pageNumber < numPages && (
              <Button variant="ghost" size="icon" onClick={goToNextPage}>
                <CaretRight size={16} weight="bold" />
              </Button>
            )}
          </div>
        )}

        <div className="flex items-center gap-3">
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <div className="flex items-center gap-2" style={{ width: '120px' }}>
                <span className="text-xs text-text-subtle font-sans whitespace-nowrap">50%</span>
                <TooltipTrigger asChild>
                  <div className="flex-1">
                    <Slider value={[scale]} onValueChange={handleSliderChange} min={0.5} max={3.0} step={0.05} className="w-full" />
                  </div>
                </TooltipTrigger>
                <span className="text-xs text-text-subtle font-sans whitespace-nowrap">300%</span>
              </div>
              <TooltipContent><p>{Math.round(scale * 100)}%</p></TooltipContent>
            </Tooltip>
          </TooltipProvider>

        </div>
      </div>

      {/* PDF area */}
      <div
        ref={containerRef}
        className={`flex-1 bg-background p-4 min-h-0 overflow-auto ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
        style={{ overscrollBehavior: 'contain' }}
        onMouseDown={handleMouseDown}
      >
        <div style={{ display: 'flex', alignItems: 'center', minHeight: '100%' }}>
          {!pdfSource ? (
            <div className="text-text-subtle font-sans">No hay archivo para mostrar</div>
          ) : hasError ? (
            <div className="text-destructive font-sans text-sm">Error al cargar el archivo</div>
          ) : !optionsReady || !pageReady ? (
            <>
              <Skeleton />
              {/* Mount Document hidden so it loads in background; Page fires onRenderSuccess when ready */}
              {optionsReady && (
                <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}>
                  <Document
                    file={pdfSource}
                    onLoadSuccess={({ numPages: n }) => setNumPages(n)}
                    onLoadError={() => setHasError(true)}
                    loading={null}
                    error={null}
                    options={pdfOptions}
                  >
                    <Page
                      pageNumber={pageNumber}
                      scale={scale}
                      renderTextLayer={true}
                      renderAnnotationLayer={true}
                      onRenderSuccess={() => setPageReady(true)}
                    />
                  </Document>
                </div>
              )}
            </>
          ) : (
            <Document
              file={pdfSource}
              onLoadSuccess={({ numPages: n }) => setNumPages(n)}
              onLoadError={() => setHasError(true)}
              loading={null}
              error={null}
              options={pdfOptions}
            >
              <div ref={pageWrapperRef} style={{ position: 'relative', display: 'inline-block' }}>
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  renderTextLayer={true}
                  renderAnnotationLayer={true}
                  onRenderSuccess={() => setPageReady(true)}
                />
                {highlightRects.map((r, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: r.x,
                      top: r.y,
                      width: r.width,
                      height: r.height,
                      background: 'rgba(253, 224, 71, 0.45)',
                      pointerEvents: 'none',
                      borderRadius: 2,
                    }}
                  />
                ))}
                {highlightMatchFailed && (
                  <div style={{ position: 'absolute', top: 8, left: 8, background: 'rgba(239,68,68,0.9)', color: '#fff', fontSize: 11, padding: '3px 7px', borderRadius: 4, pointerEvents: 'none' }}>
                    No se encontró el texto en el PDF
                  </div>
                )}
              </div>
            </Document>
          )}
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
